import { compact, groupBy, sortBy, uniq } from 'lodash'
import moment from 'moment'
import type {
  CalendarAppointment,
  CalendarRange,
  CalendarRoom,
  CalendarView,
  DayColumn,
  DayCount,
  PlacedVisit,
  ProviderInfo,
  SchedulingRoomsRead,
  Visit,
  VisitTone,
} from 'app/types/appointment-calendar'
import { isAppointmentConfirmed } from 'lib/appointmentConfirmed'
import { packLanes, toMinutes } from 'lib/dayClock'

const CALENDAR_VIEWS: readonly CalendarView[] = [`day`, `week`, `month`]

const DAY = `YYYY-MM-DD`

export function calendarView(param: string | null): CalendarView {
  return CALENDAR_VIEWS.includes(param as CalendarView) ? (param as CalendarView) : `day`
}

/** The `day` param when it is a real date, today otherwise. */
export function calendarDay(param: string | null, today: Date): string {
  const parsed = moment(param ?? ``, DAY, true)
  return parsed.isValid() ? parsed.format(DAY) : moment(today).format(DAY)
}

/** Weeks run Monday to Sunday, the way a practice books them. */
export function calendarRange(view: CalendarView, day: string): CalendarRange {
  const anchor = moment(day, DAY)
  const start =
    view === `day`
      ? anchor.clone()
      : view === `week`
        ? anchor.clone().startOf(`isoWeek`)
        : anchor.clone().startOf(`month`).startOf(`isoWeek`)
  const end =
    view === `day`
      ? anchor.clone()
      : view === `week`
        ? anchor.clone().endOf(`isoWeek`)
        : anchor.clone().endOf(`month`).endOf(`isoWeek`)
  const days: string[] = []
  for (const cursor = start.clone(); cursor.isSameOrBefore(end, `day`); cursor.add(1, `day`))
    days.push(cursor.format(DAY))
  return { from: start.format(DAY), to: end.format(DAY), days }
}

export function rangeHeading(view: CalendarView, day: string): string {
  const anchor = moment(day, DAY)
  if (view === `day`) return anchor.format(`dddd, MMMM D, YYYY`)
  if (view === `month`) return anchor.format(`MMMM YYYY`)
  const start = anchor.clone().startOf(`isoWeek`)
  const end = anchor.clone().endOf(`isoWeek`)
  if (start.isSame(end, `month`)) return `${start.format(`MMMM D`)} to ${end.format(`D, YYYY`)}`
  if (start.isSame(end, `year`)) return `${start.format(`MMM D`)} to ${end.format(`MMM D, YYYY`)}`
  return `${start.format(`MMM D, YYYY`)} to ${end.format(`MMM D, YYYY`)}`
}

/** Cancelled and broken visits are read too, so the page can count them and draw them on request. */
export function calendarSearch(range: Pick<CalendarRange, `from` | `to`>, locationIds: readonly string[]): string {
  return compact([
    `date:[${range.from} TO ${range.to}]`,
    `!pmsStatus:deleted`,
    locationIds.length > 0 && `locationId:(${locationIds.join(` OR `)})`,
  ]).join(` AND `)
}

export function visitTone(appointment: CalendarAppointment, now: Date): VisitTone {
  if (appointment.status === `Cancelled`) return `cancelled`
  if (appointment.status === `Broken`) return `broken`
  if (appointment.status === `Completed`) return `completed`
  return isAppointmentConfirmed(appointment, now) ? `confirmed` : `scheduled`
}

export const CALLED_OFF_TONES: ReadonlySet<VisitTone> = new Set([`cancelled`, `broken`])

/** A visit with no length still needs room to be seen and clicked. */
const MIN_VISIT_MINUTES = 15

/**
 * A hygiene visit lists the dentist who does the exam first and the hygienist whose chair it is
 * second, so the column goes to the hygienist when one is listed. Without taxonomy to go on,
 * the last listed provider is the one in the chair.
 */
function chairProvider(
  providerIds: readonly string[],
  isHygienist: (providerId: string) => boolean,
): string | undefined {
  return providerIds.find(isHygienist) ?? providerIds.at(-1)
}

export function toVisits(
  appointments: readonly CalendarAppointment[],
  opts: { now: Date; isHygienist: (locationId: string | null | undefined, providerId: string) => boolean },
): Visit[] {
  const visits = appointments.flatMap((appointment): Visit[] => {
    const start = toMinutes(appointment.time ?? ``)
    if (!appointment.date || start === undefined) return []
    const providerIds = compact((appointment.providerId ?? ``).split(`,`).map((id) => id.trim()))
    return [
      {
        appointment,
        // `date` is the visit's day at UTC midnight and `time` the practice's clock: read as text, never shifted.
        day: appointment.date.slice(0, 10),
        start,
        end: start + Math.max(Number(appointment.duration) || 0, MIN_VISIT_MINUTES),
        tone: visitTone(appointment, opts.now),
        providerIds,
        chairProviderId: chairProvider(providerIds, (id) => opts.isHygienist(appointment.locationId, id)),
      },
    ]
  })
  return sortBy(visits, [`day`, `start`, `end`])
}

const DEFAULT_START = 8 * 60
const DEFAULT_END = 17 * 60

/** The hours a day board draws: a working day, stretched to whole hours around anything booked outside it. */
export function dayBounds(visits: readonly Pick<Visit, `start` | `end`>[]): { start: number; end: number } {
  const start = Math.min(DEFAULT_START, ...visits.map((visit) => Math.floor(visit.start / 60) * 60))
  const end = Math.max(DEFAULT_END, ...visits.map((visit) => Math.ceil(visit.end / 60) * 60))
  return { start, end: Math.min(end, 24 * 60) }
}

type Describe = (locationId: string | null | undefined, providerId: string) => ProviderInfo | undefined

/** One column per chair provider, dentists before hygienists, each group by name; unassigned work last. */
export function providerColumns(visits: readonly Visit[], describe: Describe): DayColumn[] {
  const groups = Object.values(
    groupBy(visits, (visit) => `${visit.appointment.locationId ?? ``}|${visit.chairProviderId ?? ``}`),
  )
  const columns = groups.map((group) => {
    const [first] = group
    const providerId = first.chairProviderId
    const provider = providerId ? describe(first.appointment.locationId, providerId) : undefined
    return {
      providerId,
      hygienist: provider?.hygienist ?? false,
      column: {
        key: `${first.appointment.locationId ?? ``}|${providerId ?? ``}`,
        title: provider?.name ?? providerId ?? `No provider`,
        subtitle: provider?.role,
        visits: packLanes(group),
      },
    }
  })
  return sortBy(columns, [
    (entry) => (entry.providerId ? 0 : 1),
    (entry) => (entry.hygienist ? 1 : 0),
    (entry) => entry.column.title.toLowerCase(),
  ]).map((entry) => entry.column)
}

/**
 * Where each visit sits, from the scheduling call: the GraphQL appointment has no room. A synced
 * id is the PMS id behind a `K-<consumer>-` prefix, which is the id the scheduling call returns.
 */
export function roomLookup(reads: readonly SchedulingRoomsRead[]): (visit: Visit) => CalendarRoom | undefined {
  const rooms = new Map<string, CalendarRoom>()
  for (const read of reads) {
    const names = new Map(read.operatories.map((room, order) => [room.id, { name: room.name?.trim(), order }]))
    for (const appointment of read.appointments) {
      if (!appointment.operatoryId) continue
      const room = names.get(appointment.operatoryId)
      rooms.set(`${read.locationId}|${appointment.id}`, {
        key: `${read.locationId}|${appointment.operatoryId}`,
        name: room?.name || `Room ${appointment.operatoryId}`,
        order: room?.order ?? Number.MAX_SAFE_INTEGER,
      })
    }
  }
  return (visit) => rooms.get(`${visit.appointment.locationId ?? ``}|${visit.appointment.id.split(`-`).at(-1)}`)
}

/** One column per room with work in it, in the PMS's room order, each named for who is in its chair. */
export function roomColumns(
  visits: readonly Visit[],
  roomOf: (visit: Visit) => CalendarRoom | undefined,
  describe: Describe,
): DayColumn[] {
  const groups = Object.values(groupBy(visits, (visit) => roomOf(visit)?.key ?? ``))
  const columns = groups.map((group) => {
    const room = roomOf(group[0])
    const people = uniq(
      compact(
        group.map(
          (visit) =>
            visit.chairProviderId &&
            (describe(visit.appointment.locationId, visit.chairProviderId)?.name ?? visit.chairProviderId),
        ),
      ),
    )
    return {
      order: room?.order ?? Number.MAX_SAFE_INTEGER,
      column: {
        key: room?.key ?? `no-room`,
        title: room?.name ?? `No room`,
        subtitle: people.join(`, `) || undefined,
        visits: packLanes(group),
      },
    }
  })
  return sortBy(columns, [(entry) => (entry.column.key === `no-room` ? 1 : 0), `order`]).map((entry) => entry.column)
}

/** PMS names arrive "Last, First" (sometimes "Last, D.D.S., First"); people read them first name first. */
export function providerFullName(pmsName: string | null | undefined): string | undefined {
  const parts = compact((pmsName ?? ``).split(`,`).map((part) => part.trim()))
  if (parts.length < 2) return parts[0]
  const [last, ...rest] = parts
  const first = rest.at(-1)!
  const suffixes = rest.slice(0, -1)
  return [`${first} ${last}`, ...suffixes].join(`, `)
}

export function dayCounts(visits: readonly Pick<Visit, `day` | `tone`>[]): Map<string, DayCount> {
  const counts = new Map<string, DayCount>()
  for (const visit of visits) {
    const count = counts.get(visit.day) ?? { booked: 0, calledOff: 0 }
    if (CALLED_OFF_TONES.has(visit.tone)) count.calledOff++
    else count.booked++
    counts.set(visit.day, count)
  }
  return counts
}
